import { Injectable } from '@angular/core';
import { McticHttpClient } from '@mctic/ng-core';
import { Observable } from 'rxjs';
import { map, publishReplay, refCount } from 'rxjs/operators';
import { TipoParametroEnum } from '../shared/enum/tipo-parametro.enum';
import { Regiao, Identity, ParametroCartaCredito } from '../shared/model';
import { Parametrizacao } from '../shared/model/parametrizacao.model';
import { ParametrizacaoVO } from '../shared/model/parametrizacao-vo.model';
import { RegiaoEnum } from '../shared/enum/regiao.enum';

@Injectable({
  providedIn: 'root'
})
export class ParametrizacaoService {

  private _regiao: Regiao;
  private _valorProdutoObservable: Observable<Parametrizacao>;
  private _obrigacaoAplicadaInferiorObservable: Observable<Parametrizacao>;
  private _obrigacaoAplicadaSuperiorObservable: Observable<Parametrizacao>;
  private _obrigacaoAplicadaObservable: Observable<Parametrizacao>;
  private readonly service = '/sepin-backend/rest/parametrizacoes';

  constructor(private readonly http: McticHttpClient) {
  }

  private static regiaoMap(parametrizacoes: Parametrizacao[], regiao: Regiao) {
    return parametrizacoes.find(parametrizacao => parametrizacao.regiao.id === regiao.id);
  }

  obterValorProduto(regiao: Regiao): Observable<Parametrizacao> {
    if (this._valorProdutoObservable === undefined || this._regiao.id !== regiao.id) {
      this._regiao = regiao;

      this._valorProdutoObservable = this.obterParametrizacoes(TipoParametroEnum.ValorProduto).pipe(
        map(value => ParametrizacaoService.regiaoMap(value, regiao)),
        publishReplay(1),
        refCount()
      );
    }

    return this._valorProdutoObservable;
  }

  obterObrigacaoAplicadaInferior(regiao: Regiao): Observable<Parametrizacao> {
    if (this._obrigacaoAplicadaInferiorObservable === undefined || this._regiao.id !== regiao.id) {
      this._regiao = regiao;

      this._obrigacaoAplicadaInferiorObservable = this.obterParametrizacoes(TipoParametroEnum.ObrigacaoAplicadaInferior).pipe(
        map(value => ParametrizacaoService.regiaoMap(value, regiao)),
        publishReplay(1),
        refCount()
      );
    }

    return this._obrigacaoAplicadaInferiorObservable;
  }

  obterObrigacaoAplicadaSuperior(regiao: Regiao): Observable<Parametrizacao> {
    if (this._obrigacaoAplicadaSuperiorObservable === undefined || this._regiao.id !== regiao.id) {
      this._regiao = regiao;

      this._obrigacaoAplicadaSuperiorObservable = this.obterParametrizacoes(TipoParametroEnum.ObrigacaoAplicadaSuperior).pipe(
        map(value => ParametrizacaoService.regiaoMap(value, regiao)),
        publishReplay(1),
        refCount()
      );
    }

    return this._obrigacaoAplicadaSuperiorObservable;
  }

  obterPercentualObrigacaoAplicacao(idTipoParametro:number, idRegiao: number): Observable<Parametrizacao> {
    return this.http.get<Parametrizacao>(
      `${this.service}/tipo/${idTipoParametro}/regiao/${idRegiao}`
    );
  }

  obterObrigacaoAplicada(regiao: Regiao): Observable<Parametrizacao> {
    if (this._obrigacaoAplicadaObservable === undefined || this._regiao.id !== regiao.id) {
      this._regiao = regiao;

      this._obrigacaoAplicadaObservable = this.obterParametrizacoes(TipoParametroEnum.ObrigacaoAplicada).pipe(
        map(value => ParametrizacaoService.regiaoMap(value, regiao)),
        publishReplay(1),
        refCount()
      );
    }

    return this._obrigacaoAplicadaObservable;
  }

  obterParametrizacoes(tipoParametro: TipoParametroEnum): Observable<Parametrizacao[]> {
    return this.http.get(`/sepin-backend/rest/tipos-parametros/${tipoParametro}/parametrizacoes`, { reportProgress: false });
  }

  obterParametrizacoesAnoBaseVigente(): Observable<Parametrizacao[]> {
    return this.http.get(`${this.service}/ano-base-vigente`);
  }

  salvar(configuracaoParametro: ParametrizacaoVO): Observable<Identity<number>> {
    return this.http.post<Identity<number>>(this.service, configuracaoParametro);
  }

  alterar(configuracaoParametro: ParametrizacaoVO) {
    return this.http.put<Identity<number>>(this.service, configuracaoParametro);
  }

  obterParametrizacaoPorAnoBaseRegiao(idAnoBase: number, idRegiao: number): Observable<Parametrizacao[]> {
    return this.http.get<Parametrizacao[]>(`${this.service}/ano-base/${idAnoBase}/regiao/${idRegiao}`)
  }

  filtrarParametrizacaoCartaCreditoTrimestral(parametrizacoes: Parametrizacao[]):
    ParametroCartaCredito {
    let parametrizacaoCarta: ParametroCartaCredito = new ParametroCartaCredito();
    parametrizacaoCarta.multiplicadorSudanSudeneCentroOeste =
      this.obterParametroCartaCreditoSudanSudeneCentroOeste(parametrizacoes,
        TipoParametroEnum.MultiplicadorCartaCredito
      );
    parametrizacaoCarta.multiplicadorSudanSudeneCentroOesteTecnac =
      this.obterParametroCartaCreditoSudanSudeneCentroOeste(parametrizacoes,
        TipoParametroEnum.MultiplicadorCartaCreditoTecnac
      );
    parametrizacaoCarta.multiplicadorDemaisRegioes =
      this.obterParametroCartaCreditoDemaisRegioes(parametrizacoes,
        TipoParametroEnum.MultiplicadorCartaCredito);

    parametrizacaoCarta.multiplicadorDemaisRegioesTecnac =
      this.obterParametroCartaCreditoDemaisRegioes(parametrizacoes,
        TipoParametroEnum.MultiplicadorCartaCreditoTecnac);

    parametrizacaoCarta.percentualSudanSudeneCentroOeste =
      this.obterParametroCartaCreditoSudanSudeneCentroOeste(parametrizacoes,
        TipoParametroEnum.PercentualLimite);

    parametrizacaoCarta.percentualSudanSudeneCentroOesteTecnac =
      this.obterParametroCartaCreditoSudanSudeneCentroOeste(parametrizacoes,
        TipoParametroEnum.PercentualLimiteTecnac);

    parametrizacaoCarta.percentualDemaisRegioesTecnac =
      this.obterParametroCartaCreditoDemaisRegioes(parametrizacoes,
        TipoParametroEnum.PercentualLimiteTecnac);

    parametrizacaoCarta.percentualDemaisRegioes =
      this.obterParametroCartaCreditoDemaisRegioes(parametrizacoes,
        TipoParametroEnum.PercentualLimite);

    parametrizacaoCarta.multiplicadorPadis =
      this.obterParametroCartaCreditoPadis(parametrizacoes, TipoParametroEnum.MultiplicadorPadis);

    parametrizacaoCarta.percentualPadis =
      this.obterParametroCartaCreditoPadis(parametrizacoes, TipoParametroEnum.PercentualPadis);

    return parametrizacaoCarta;

  }

  filtrarParametrizacaoCartaCreditoAnual(parametrizacoes: Parametrizacao[]):
    ParametroCartaCredito {
    let parametrizacaoCarta: ParametroCartaCredito = new ParametroCartaCredito();

    parametrizacaoCarta.multiplicadorSudanSudeneCentroOeste =
      this.obterParametroCartaCreditoSudanSudeneCentroOeste(parametrizacoes,
        TipoParametroEnum.MultiplicadorAnual);

    parametrizacaoCarta.multiplicadorDemaisRegioes =
      this.obterParametroCartaCreditoDemaisRegioes(parametrizacoes,
        TipoParametroEnum.MultiplicadorAnual);

    parametrizacaoCarta.percentualSudanSudeneCentroOeste =
      this.obterParametroCartaCreditoSudanSudeneCentroOeste(parametrizacoes,
        TipoParametroEnum.PercentualLimiteAnual);

    parametrizacaoCarta.percentualDemaisRegioes =
      this.obterParametroCartaCreditoDemaisRegioes(parametrizacoes,
        TipoParametroEnum.PercentualLimiteAnual);

    parametrizacaoCarta.denominadorPdicSudanSudeneCentroOeste =
      this.obterParametroCartaCreditoSudanSudeneCentroOeste(parametrizacoes,
        TipoParametroEnum.DenominadorPdic);

    parametrizacaoCarta.denominadorPdicDemaisRegioes =
      this.obterParametroCartaCreditoDemaisRegioes(parametrizacoes,
        TipoParametroEnum.DenominadorPdic);

    return parametrizacaoCarta;
  }

  obterParametroCartaCreditoSudanSudeneCentroOeste(
    parametrizacoes: Parametrizacao[],
    tipoParametro: TipoParametroEnum) {
    return parametrizacoes.find(parametro => (
      parametro.regiao.id == RegiaoEnum.SUDAM ||
      parametro.regiao.id == RegiaoEnum.SUDENE ||
      parametro.regiao.id == RegiaoEnum.CENTRO_OESTE) &&
      parametro.tipoParametro.id == tipoParametro).valor;
  }

  obterParametroCartaCreditoDemaisRegioes(parametrizacoes: Parametrizacao[],
    tipoParametro: TipoParametroEnum) {
    return parametrizacoes.find(parametro => (
      parametro.regiao.id == RegiaoEnum.DEMAIS_REGIOES) &&
      parametro.tipoParametro.id == tipoParametro).valor;
  }

  obterParametroCartaCreditoPadis(parametrizacoes: Parametrizacao[],
    tipoParametro: TipoParametroEnum) {
    return parametrizacoes.find(parametro =>
      parametro.tipoParametro.id == tipoParametro).valor;
  }
}
